<!-- =====================================================
     Two-Factor Setup Component Template
     ===================================================== -->

<div class="two-factor-settings">
  <h3>Autentificare în doi pași (2FA)</h3>

  <!-- Loading -->
  <div *ngIf="loading" class="loading-overlay">
    <div class="spinner"></div>
  </div>

  <!-- Messages -->
  <div *ngIf="error" class="alert alert-danger">{{ error }}</div>
  <div *ngIf="success" class="alert alert-success">{{ success }}</div>

  <!-- ========== STATUS VIEW ========== -->
  <div *ngIf="currentStep === 'status'" class="status-view">
    <div class="status-card" [class.enabled]="totpEnabled">
      <div class="status-icon">
        <i [class]="totpEnabled ? 'fa fa-shield-alt' : 'fa fa-shield'"></i>
      </div>
      <div class="status-info">
        <h4>{{ totpEnabled ? 'Activată' : 'Dezactivată' }}</h4>
        <p *ngIf="totpEnabled">
          Contul dvs. este protejat cu autentificare în doi pași.
        </p>
        <p *ngIf="!totpEnabled">
          Adăugați un nivel suplimentar de securitate contului dvs.
        </p>
      </div>
    </div>

    <!-- Actions -->
    <div class="actions">
      <button 
        *ngIf="!totpEnabled" 
        class="btn btn-primary"
        (click)="startSetup()"
        [disabled]="loading"
      >
        <i class="fa fa-plus"></i> Activează 2FA
      </button>

      <button 
        *ngIf="totpEnabled" 
        class="btn btn-danger"
        (click)="openDisableModal()"
      >
        <i class="fa fa-times"></i> Dezactivează 2FA
      </button>
    </div>

    <!-- Trusted Devices Section -->
    <div *ngIf="totpEnabled" class="devices-section">
      <h4>Dispozitive de încredere</h4>
      <p>{{ trustedDevicesCount }} dispozitiv(e) nu necesită cod 2FA</p>
      <button class="btn btn-outline" (click)="showTrustedDevices()">
        <i class="fa fa-laptop"></i> Gestionează dispozitivele
      </button>
    </div>
  </div>

  <!-- ========== QR CODE STEP ========== -->
  <div *ngIf="currentStep === 'qrcode'" class="setup-step">
    <div class="step-header">
      <span class="step-number">1</span>
      <h4>Scanați codul QR</h4>
    </div>

    <div class="qr-container">
      <img 
        *ngIf="setupData?.qr_code" 
        [src]="'data:image/png;base64,' + setupData.qr_code" 
        alt="QR Code"
        class="qr-image"
      />
    </div>

    <p class="instructions">
      Scanați acest cod QR cu aplicația de autentificare 
      (Google Authenticator, Authy, Microsoft Authenticator, etc.)
    </p>

    <div class="manual-entry">
      <p>Nu puteți scana? Introduceți manual acest cod:</p>
      <div class="secret-code">
        <code>{{ setupData?.secret }}</code>
        <button 
          class="btn-icon" 
          (click)="copySecret()"
          title="Copiază"
        >
          <i class="fa fa-copy"></i>
        </button>
      </div>
    </div>

    <div class="step-actions">
      <button class="btn btn-outline" (click)="backToStatus()">Anulează</button>
      <button class="btn btn-primary" (click)="proceedToVerify()">Continuă</button>
    </div>
  </div>

  <!-- ========== VERIFY STEP ========== -->
  <div *ngIf="currentStep === 'verify'" class="setup-step">
    <div class="step-header">
      <span class="step-number">2</span>
      <h4>Verificați configurarea</h4>
    </div>

    <p class="instructions">
      Introduceți codul de 6 cifre afișat în aplicația de autentificare 
      pentru a confirma configurarea.
    </p>

    <form [formGroup]="verifyForm" (ngSubmit)="verifySetup()">
      <div class="form-group">
        <input 
          type="text" 
          formControlName="code"
          class="form-control code-input"
          placeholder="000000"
          maxlength="6"
          autocomplete="one-time-code"
        />
      </div>

      <div class="step-actions">
        <button type="button" class="btn btn-outline" (click)="currentStep = 'qrcode'">
          Înapoi
        </button>
        <button 
          type="submit" 
          class="btn btn-primary"
          [disabled]="verifyForm.invalid || loading"
        >
          Verifică
        </button>
      </div>
    </form>
  </div>

  <!-- ========== BACKUP CODES STEP ========== -->
  <div *ngIf="currentStep === 'backup'" class="setup-step">
    <div class="step-header">
      <span class="step-number">3</span>
      <h4>Salvați codurile de rezervă</h4>
    </div>

    <div class="alert alert-warning">
      <i class="fa fa-exclamation-triangle"></i>
      Salvați aceste coduri într-un loc sigur! Le veți folosi dacă pierdeți accesul la aplicația de autentificare.
    </div>

    <div class="backup-codes-grid">
      <div *ngFor="let code of backupCodes" class="backup-code">
        {{ code }}
      </div>
    </div>

    <div class="backup-actions">
      <button class="btn btn-outline" (click)="copyBackupCodes()">
        <i class="fa fa-copy"></i> Copiază
      </button>
      <button class="btn btn-outline" (click)="downloadBackupCodes()">
        <i class="fa fa-download"></i> Descarcă
      </button>
    </div>

    <div class="step-actions">
      <button class="btn btn-primary" (click)="completeSetup()">
        Am salvat codurile
      </button>
    </div>
  </div>

  <!-- ========== COMPLETE STEP ========== -->
  <div *ngIf="currentStep === 'complete'" class="setup-step complete-step">
    <div class="success-icon">
      <i class="fa fa-check-circle"></i>
    </div>
    <h4>Autentificare în doi pași activată!</h4>
    <p>Contul dvs. este acum protejat cu un nivel suplimentar de securitate.</p>
    <button class="btn btn-primary" (click)="backToStatus()">Închide</button>
  </div>
</div>

<!-- ========== DISABLE MODAL ========== -->
<div *ngIf="showDisableModal" class="modal-overlay" (click)="closeDisableModal()">
  <div class="modal-content" (click)="$event.stopPropagation()">
    <h4>Dezactivare 2FA</h4>
    <p>Introduceți parola pentru a confirma dezactivarea autentificării în doi pași.</p>
    
    <form [formGroup]="disableForm" (ngSubmit)="disable2FA()">
      <div class="form-group">
        <label>Parola</label>
        <input 
          type="password" 
          formControlName="password"
          class="form-control"
          placeholder="Introduceți parola"
        />
      </div>
      
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" (click)="closeDisableModal()">
          Anulează
        </button>
        <button 
          type="submit" 
          class="btn btn-danger"
          [disabled]="disableForm.invalid || loading"
        >
          Dezactivează
        </button>
      </div>
    </form>
  </div>
</div>

<!-- ========== DEVICES MODAL ========== -->
<div *ngIf="showDevicesModal" class="modal-overlay" (click)="closeDevicesModal()">
  <div class="modal-content modal-large" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h4>Dispozitive de încredere</h4>
      <button class="btn-close" (click)="closeDevicesModal()">&times;</button>
    </div>
    
    <div class="devices-list">
      <div *ngIf="trustedDevices.length === 0" class="no-devices">
        Nu există dispozitive de încredere.
      </div>
      
      <div *ngFor="let device of trustedDevices" class="device-item">
        <div class="device-info">
          <div class="device-name">
            <i class="fa fa-laptop"></i>
            {{ device.device_name }}
          </div>
          <div class="device-details">
            <span>IP: {{ device.ip_address }}</span>
            <span>Ultima utilizare: {{ formatDate(device.last_used_at) }}</span>
            <span>Expiră: {{ formatDate(device.expires_at) }}</span>
          </div>
        </div>
        <button class="btn btn-sm btn-danger" (click)="revokeDevice(device.id)">
          Revocă
        </button>
      </div>
    </div>
    
    <div *ngIf="trustedDevices.length > 1" class="modal-footer">
      <button class="btn btn-outline-danger" (click)="revokeAllDevices()">
        Revocă toate celelalte dispozitive
      </button>
    </div>
  </div>
</div>
